/**
 * Government Data Dashboard
 *
 * Unified dashboard combining Integrations, Awards, and Industry News tabs.
 * Provides a single interface for managing and monitoring all government data sources.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, PageHeader, PageContainer, useConfig, useAuth } from '@tracekit/core';
import { Globe } from 'lucide-react';
import { ExternalDataProvider } from '../../contexts/ExternalDataContext';
import { IntegrationsTab } from '../../components/government-data/ExternalData/IntegrationsTab';
import { NewsTab } from '../../components/government-data/ExternalData/NewsTab';
import { GlobalOpportunitiesTab } from '../../components/government-data/ExternalData/GlobalOpportunitiesTab';
import { OpportunitiesTab } from '../../components/government-data/ExternalData/OpportunitiesTab';
import { GeographicZoneToggleBar } from '../../components/government-data/GeographicZoneToggleBar';

/** Legacy tab ids kept for bookmarked URLs. */
export const GOVERNMENT_DATA_TAB_ALIASES: Record<string, string> = {
  'global-opportunities': 'weekly-review',
};

export function resolveGovernmentDataTabId(tab: string | null | undefined): string {
  if (!tab) return 'opportunities';
  return GOVERNMENT_DATA_TAB_ALIASES[tab] ?? tab;
}

const GOVERNMENT_DATA_TAB_HELP: Record<string, string> = {
  opportunities: 'rfp-opportunities-tab',
  'weekly-review': 'rfp-weekly-review-tab',
  news: 'rfp-news-tab',
  integrations: 'rfp-integrations-tab',
};

function GovernmentDataDashboardContent() {
  const config = useConfig();
  const { user: authUser } = useAuth();
  const readTabFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return resolveGovernmentDataTabId(urlParams.get('tab'));
  };

  const [activeTab, setActiveTab] = useState(readTabFromUrl);

  const canAccessWeeklyReview = useMemo(() => {
    const flags = config?.customization?.featureFlags;
    if (flags?.enableWeeklyReview === true) {
      return true;
    }
    const devEmails = (config?.customization?.weeklyReviewDevEmails as string[] | undefined) ?? [];
    const userEmail = authUser?.email?.toLowerCase?.();
    return Boolean(userEmail && devEmails.some((e) => e.toLowerCase() === userEmail));
  }, [
    config?.customization?.featureFlags?.enableWeeklyReview,
    config?.customization?.weeklyReviewDevEmails,
    authUser?.email,
  ]);

  useEffect(() => {
    const syncTabFromUrl = () => {
      setActiveTab(readTabFromUrl());
    };
    window.addEventListener('popstate', syncTabFromUrl);
    return () => window.removeEventListener('popstate', syncTabFromUrl);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const rawTab = urlParams.get('tab');
    if (!rawTab) return;

    const resolvedTab = resolveGovernmentDataTabId(rawTab);
    if (resolvedTab !== rawTab) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', resolvedTab);
      window.history.replaceState({}, '', url.toString());
      setActiveTab(resolvedTab);
    }
  }, []);

  // Harden direct URL access to weekly-review when user lacks permission.
  useEffect(() => {
    if (activeTab === 'weekly-review' && !canAccessWeeklyReview) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'opportunities');
      window.history.replaceState({}, '', url.toString());
      setActiveTab('opportunities');
    }
  }, [activeTab, canAccessWeeklyReview]);

  const handleTabChange = (tabId: string) => {
    if (tabId === 'weekly-review' && !canAccessWeeklyReview) {
      return;
    }
    setActiveTab(tabId);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabId);
    window.history.pushState({}, '', url.toString());
  };

  const pageTitle = config.customization?.opportunitiesDashboardLabel ?? 'Government Data Center';
  const showGeographicZones =
    config.customization?.featureFlags?.enableGovernmentDataGeographicZones ??
    config.customization?.featureFlags?.showGovernmentDataZoneButtons ??
    false;

  const tabs = useMemo(() => {
    const allTabs = [
      {
        id: 'opportunities',
        label: 'Opportunities',
        helpArticleId: GOVERNMENT_DATA_TAB_HELP.opportunities,
        content: <OpportunitiesTab />,
      },
      {
        id: 'weekly-review',
        label: 'Recommended Tenders',
        helpArticleId: GOVERNMENT_DATA_TAB_HELP['weekly-review'],
        content: <GlobalOpportunitiesTab />,
      },
      {
        id: 'news',
        label: 'Industry News',
        helpArticleId: GOVERNMENT_DATA_TAB_HELP.news,
        content: <NewsTab />,
      },
      {
        id: 'integrations',
        label: 'Data Integrations',
        helpArticleId: GOVERNMENT_DATA_TAB_HELP.integrations,
        content: <IntegrationsTab />,
      },
    ];
    return allTabs.filter((tab) => tab.id !== 'weekly-review' || canAccessWeeklyReview);
  }, [canAccessWeeklyReview]);

  return (
    <PageContainer>
      <PageHeader
        icon={<Globe className="h-8 w-8" aria-hidden="true" />}
        title={pageTitle}
        subtitle="Monitor and manage government data integrations for RFP intelligence"
        helpArticleId="rfp-government-data-dashboard"
        actions={showGeographicZones ? <GeographicZoneToggleBar compact /> : undefined}
      />

      <Tabs
        tabs={tabs}
        defaultTab={activeTab}
        activeTab={activeTab}
        onChange={handleTabChange}
      />
    </PageContainer>
  );
}

export function GovernmentDataDashboard() {
  return (
    <ExternalDataProvider>
      <GovernmentDataDashboardContent />
    </ExternalDataProvider>
  );
}
